import { useState } from "react";
import { toast } from "sonner";
import { Phone, Navigation, Mail, MapPin, Car, Instagram, Facebook, BookOpen, Globe } from "lucide-react";
import { BRAND } from "@/data/media";
import { SITE } from "@/data/site";
import { Reveal, LineReveal } from "@/components/motion";

const MAP_EMBED =
  "https://www.google.com/maps?q=197-201+High+Street+Hornchurch+Essex+RM11+3XT&output=embed";
const MAP_DIR =
  "https://www.google.com/maps/dir/?api=1&destination=197-201+High+Street+Hornchurch+Essex+RM11+3XT";

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setSent(true);
    toast.success("Thank you — your note has reached the house. We'll be in touch shortly.");
    setForm({ name: "", email: "", phone: "", message: "" });
    setTimeout(() => setSent(false), 4000);
  };

  const field =
    "w-full bg-transparent border-b border-cream/25 focus:border-gold py-3 font-body font-light text-cream placeholder:text-cream/35 outline-none transition-colors";

  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-7">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input name="name" value={form.name} onChange={onChange} placeholder="Your name" data-testid="contact-name" className={field} />
        <input name="email" type="email" value={form.email} onChange={onChange} placeholder="Email" data-testid="contact-email" className={field} />
      </div>
      <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone (optional)" data-testid="contact-phone" className={field} />
      <textarea
        name="message"
        value={form.message}
        onChange={onChange}
        rows={4}
        placeholder="Tell us how we can help — an enquiry, a large table, a special occasion…"
        data-testid="contact-message"
        className={field + " resize-none"}
      />
      <button
        type="submit"
        data-testid="contact-submit"
        className="btn-veedu border-gold/60 text-gold hover:bg-gold hover:text-ink"
      >
        {sent ? "Message Sent" : "Contact Us"}
      </button>
    </form>
  );
}

export default function Contact() {
  return (
    <div className="bg-ink text-cream" data-testid="page-contact">
      {/* hero */}
      <section className="pt-28 md:pt-32 pb-12 u-container" data-testid="contact-hero">
        <Reveal>
          <p className="u-eyebrow text-terracotta mb-4">Come Home</p>
        </Reveal>
        <LineReveal
          trigger="load"
          lines={["Come and listen", "to the food tales", "of Kerala."]}
          lineClassName="font-display text-4xl md:text-7xl leading-[0.95]"
        />
      </section>

      {/* details + building */}
      <section className="u-container pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
        <div className="lg:col-span-5 space-y-10 flex flex-col justify-between">
          <Reveal>
            <div>
              <p className="u-eyebrow text-gold mb-4 flex items-center gap-2"><MapPin size={15} /> The House</p>
              <address className="not-italic font-display text-3xl md:text-4xl leading-snug">
                {SITE.address.line1}
                <br />
                {SITE.address.city}
                <br />
                {SITE.address.county} {SITE.address.postcode}
              </address>
              <p className="font-body font-light text-cream/70 mt-5 flex items-start gap-2 text-sm leading-relaxed">
                <Car size={16} className="text-gold shrink-0 mt-0.5" /> {SITE.parking}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="border-t border-cream/12 pt-6">
              <p className="u-eyebrow text-gold mb-2">{SITE.chef.name}</p>
              <p className="font-script text-2xl text-cream/80 mb-4">{SITE.chef.role}</p>
              <div className="space-y-2 font-body font-light text-cream/70 text-sm">
                <p>Director · {SITE.phones.director}</p>
                <p>Restaurant · {SITE.phones.restaurant}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="flex flex-wrap gap-3 pt-2">
              <a href={`tel:${SITE.phones.restaurant.replace(/[^+\d]/g, "")}`} data-testid="contact-call" className="btn-veedu border-cream/40 text-cream hover:bg-terracotta hover:border-terracotta py-2.5 px-5 text-xs">
                <Phone size={14} /> Call
              </a>
              <a href={MAP_DIR} target="_blank" rel="noreferrer" data-testid="contact-directions" className="btn-veedu border-cream/40 text-cream hover:bg-forest hover:border-forest py-2.5 px-5 text-xs">
                <Navigation size={14} /> Get Directions
              </a>
              <a href="#enquire" data-testid="contact-scroll-form" className="btn-veedu border-gold/50 text-gold hover:bg-gold hover:text-ink py-2.5 px-5 text-xs">
                <Mail size={14} /> Contact Us
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1} className="h-full">
            <div className="relative overflow-hidden rounded-xl border border-gold/30 bg-charcoal h-full min-h-[380px] lg:min-h-[460px] shadow-2xl group">
              <img
                src="/images/veedu3.png"
                alt="197-201 High Street, Hornchurch - The House of #VEEDU"
                className="w-full h-full object-cover object-center transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center justify-between">
                <div>
                  <span className="u-eyebrow text-gold">197–201 High Street · Hornchurch</span>
                  <p className="font-script text-cream text-2xl mt-1">Kerala meets Essex</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* map */}
      <section className="u-container pb-24" data-testid="contact-map">
        <Reveal>
          <div className="border border-gold/30 overflow-hidden">
            <iframe
              title="Map to Chef Jomon's #VEEDU, Hornchurch"
              src={MAP_EMBED}
              className="w-full h-[360px] md:h-[460px] grayscale contrast-110 brightness-90"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </section>

      {/* enquiry form */}
      <section id="enquire" className="scroll-mt-24 bg-charcoal py-24 md:py-32" data-testid="contact-enquire">
        <div className="u-container grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="u-eyebrow text-gold mb-4">Enquire</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight">
                A note to the house.
              </h2>
              <p className="font-body font-light text-cream/60 mt-6 leading-relaxed max-w-sm">
                Enquiries, large tables and special occasions — leave a few words and we'll return your message
                personally.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.15}>
              <ContactForm />
            </Reveal>
          </div>
        </div>
      </section>

      {/* social */}
      <section className="py-24 md:py-32 u-container text-center" data-testid="contact-social">
        <Reveal>
          <p className="u-eyebrow text-terracotta mb-6">Follow the House</p>
          <h2 className="font-display text-4xl md:text-6xl mb-4">Follow Chef Jomon & #VEEDU</h2>
          <p className="font-body font-light text-cream/60 max-w-xl mx-auto mb-12">
            Follow the story as it continues — new dishes, the kitchen, and life inside the house.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
            {SITE.social.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`contact-social-${s.label.toLowerCase()}`}
                className="group inline-flex items-center gap-2 u-eyebrow text-cream/70 hover:text-gold transition-colors"
              >
                {s.label === "Instagram" && <Instagram size={18} strokeWidth={1.4} />}
                {s.label === "Facebook" && <Facebook size={18} strokeWidth={1.4} />}
                {s.label === "Wikipedia" && <BookOpen size={18} strokeWidth={1.4} />}
                {s.label === "Website" && <Globe size={18} strokeWidth={1.4} />}
                {s.label === "X" && <span className="font-display text-lg">𝕏</span>}
                <span className="link-line">{s.label}</span>
              </a>
            ))}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
